import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { MediaPickerDialog } from "@/components/admin/MediaPickerDialog";
import { CATEGORIES, CATEGORY_LABELS } from "@/lib/format";
import { toast } from "sonner";
import { Save, ArrowLeft, ImagePlus, X, Eye } from "lucide-react";

const EMPTY = {
  title: "", kicker: "", chapo: "", body: "", category: "politik", tags: [],
  author: "", featured_image: "", image_caption: "", image_credit: "",
  status: "draft", is_featured: false, is_breaking: false, published_at: null,
  source_url: "", source_name: "",
};

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

export default function Editor() {
  const { id } = useParams();
  const isNew = !id || id === "neu";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(isNew);
  const [picker, setPicker] = useState({ open: false, resolve: null, target: "body" });
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/articles/${id}`).then((r) => {
      setForm({ ...EMPTY, ...r.data });
      setTagsInput((r.data.tags || []).join(", "));
      setLoaded(true);
    }).catch(() => { toast.error("Artikel nicht gefunden."); navigate("/admin/artikel"); });
  }, [id, isNew, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openPicker = (target) => new Promise((resolve) => {
    setPicker({ open: true, resolve, target });
  });

  const onPickBody = () => openPicker("body");

  const onSelectMedia = (url) => {
    if (picker.target === "featured") set("featured_image", url);
    else if (picker.resolve) picker.resolve(url);
    setPicker({ open: false, resolve: null, target: "body" });
  };

  const save = async (statusOverride) => {
    if (!form.title.trim()) { toast.error("Bitte einen Titel eingeben."); return; }
    if (!form.author.trim()) { toast.error("Bitte einen Autor angeben."); return; }
    setSaving(true);
    const payload = {
      ...form,
      status: statusOverride || form.status,
      tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      published_at: form.published_at || null,
    };
    try {
      let res;
      if (isNew) res = await api.post("/admin/articles", payload);
      else res = await api.put(`/admin/articles/${id}`, payload);
      toast.success("Artikel gespeichert.");
      if (isNew) navigate(`/admin/artikel/${res.data.id}`);
      else setForm({ ...EMPTY, ...res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  if (!loaded) return <AdminLayout title="Artikel bearbeiten"><p className="font-body text-muted-foreground">Lädt…</p></AdminLayout>;

  const field = "w-full border border-edborder bg-white px-4 py-2.5 text-sm outline-none focus:border-prussian";
  const lbl = "block text-xs uppercase tracking-wide text-muted-foreground font-body mb-1.5 font-semibold";

  return (
    <AdminLayout title={isNew ? "Neuer Artikel" : "Artikel bearbeiten"}>
      <button onClick={() => navigate("/admin/artikel")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-prussian mb-5 font-body">
        <ArrowLeft size={15} /> Zurück zur Übersicht
      </button>

      <form data-testid="admin-article-editor-form" onSubmit={(e) => { e.preventDefault(); save(); }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <label className={lbl}>Titel *</label>
            <input data-testid="editor-title" value={form.title} onChange={(e) => set("title", e.target.value)}
              className={`${field} font-headline text-lg`} placeholder="Überschrift des Artikels" />
          </div>
          <div>
            <label className={lbl}>Chapô (Vorspann, 2 Zeilen)</label>
            <textarea data-testid="editor-chapo" value={form.chapo} onChange={(e) => set("chapo", e.target.value)} rows={2}
              className={field} placeholder="Kurze Einleitung…" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={lbl + " mb-0"}>Fließtext *</label>
              <button type="button" onClick={() => setPreview(!preview)} className="text-xs text-prussian hover:text-gold-hover inline-flex items-center gap-1 font-body">
                <Eye size={13} /> {preview ? "Editor" : "Vorschau"}
              </button>
            </div>
            {preview ? (
              <div className="border border-edborder bg-white p-4 prose-europuls" dangerouslySetInnerHTML={{ __html: form.body || "<p class='text-muted-foreground'>Noch kein Inhalt.</p>" }} />
            ) : (
              <RichTextEditor value={form.body} onChange={(v) => set("body", v)} onPickImage={onPickBody} />
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="bg-white border border-edborder p-5 space-y-4">
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="admin-save-article-btn" disabled={saving} onClick={() => save()}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-prussian text-white text-sm font-body font-semibold py-2.5 hover:bg-prussian-dark disabled:opacity-50">
                <Save size={15} /> Speichern
              </button>
              {form.status !== "published" && (
                <button type="button" data-testid="publish-btn" disabled={saving} onClick={() => save("published")}
                  className="flex-1 bg-gold text-prussian-dark text-sm font-body font-bold py-2.5 hover:bg-gold-hover disabled:opacity-50">
                  Veröffentlichen
                </button>
              )}
            </div>
            {!isNew && (
              <a href={`/admin/artikel/${id}/vorschau`} target="_blank" rel="noreferrer" data-testid="preview-article-link"
                className="flex items-center justify-center gap-2 border border-prussian text-prussian text-sm font-body font-semibold py-2.5 hover:bg-prussian hover:text-white transition-colors">
                <Eye size={15} /> Vorschau (wie veröffentlicht)
              </a>
            )}
          </div>

          <div className="bg-white border border-edborder p-5 space-y-4">
            {form.source_url && (
              <div className="bg-gold/10 border border-gold/40 px-3 py-2 text-xs font-body">
                <span className="uppercase tracking-wide text-muted-foreground">Automatisch importiert · Quelle: </span>
                <a href={form.source_url} target="_blank" rel="noreferrer" className="text-prussian underline break-all">{form.source_name || "Original"}</a>
              </div>
            )}
            <div>
              <label className={lbl}>Status</label>
              <select data-testid="editor-status" value={form.status} onChange={(e) => set("status", e.target.value)} className={field}>
                <option value="draft">Entwurf</option>
                <option value="published">Veröffentlicht</option>
                <option value="archived">Archiviert</option>
              </select>
            </div>
            <div>
              <label className={lbl}>Veröffentlichungsdatum (auch programmierbar)</label>
              <input data-testid="editor-published-at" type="datetime-local" value={toLocalInput(form.published_at)}
                onChange={(e) => set("published_at", e.target.value ? new Date(e.target.value).toISOString() : null)} className={field} />
              <p className="text-[11px] text-muted-foreground mt-1 font-body">Liegt das Datum in der Zukunft, erscheint der Artikel automatisch erst dann.</p>
            </div>
            <div>
              <label className={lbl}>Rubrik</label>
              <select data-testid="editor-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={field}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Kicker (z. B. ANALYSE)</label>
              <input data-testid="editor-kicker" value={form.kicker} onChange={(e) => set("kicker", e.target.value)} className={field} />
            </div>
            <div>
              <label className={lbl}>Autor *</label>
              <input data-testid="editor-author" value={form.author} onChange={(e) => set("author", e.target.value)} className={field} placeholder="Name des Autors" />
            </div>
            <div>
              <label className={lbl}>Tags (kommagetrennt)</label>
              <input data-testid="editor-tags" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className={field} placeholder="europa, wahl, analyse" />
            </div>
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-sm font-body text-charcoal">
                <input data-testid="editor-featured" type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} />
                Als „Une" (Aufmacher) setzen
              </label>
              <label className="flex items-center gap-2 text-sm font-body text-charcoal">
                <input data-testid="editor-breaking" type="checkbox" checked={form.is_breaking} onChange={(e) => set("is_breaking", e.target.checked)} />
                Als Eilmeldung / Live markieren
              </label>
            </div>
          </div>

          <div className="bg-white border border-edborder p-5">
            <label className={lbl}>Titelbild</label>
            {form.featured_image ? (
              <div className="relative">
                <img src={resolveImg(form.featured_image)} alt="" className="w-full aspect-video object-cover border border-edborder" />
                <button type="button" onClick={() => set("featured_image", "")} className="absolute top-2 right-2 bg-breaking text-white w-7 h-7 flex items-center justify-center"><X size={14} /></button>
              </div>
            ) : (
              <button type="button" data-testid="pick-featured-image" onClick={() => setPicker({ open: true, resolve: null, target: "featured" })}
                className="w-full border-2 border-dashed border-edborder py-8 flex flex-col items-center gap-2 text-muted-foreground hover:border-prussian hover:text-prussian font-body text-sm">
                <ImagePlus size={22} /> Bild aus Mediathek wählen
              </button>
            )}
            <input value={form.featured_image} onChange={(e) => set("featured_image", e.target.value)} placeholder="oder Bild-URL einfügen"
              className={`${field} mt-3 text-xs`} data-testid="editor-featured-url" />
            <input value={form.image_caption} onChange={(e) => set("image_caption", e.target.value)} placeholder="Bildunterschrift" className={`${field} mt-2 text-xs`} />
            <input value={form.image_credit} onChange={(e) => set("image_credit", e.target.value)} placeholder="Bildnachweis / Credit" className={`${field} mt-2 text-xs`} />
          </div>
        </div>
      </form>

      <MediaPickerDialog open={picker.open} onOpenChange={(o) => setPicker((p) => ({ ...p, open: o }))} onSelect={onSelectMedia} />
    </AdminLayout>
  );
}
